/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

export const initialState = {
  activeFilter: {
    type: null,
    value: 'all',
    label: 'All Posts',
    icon: '/assets/all-post-filter-icon.svg',
  },
  feedFilters: null,
  posts: [],
  shouldRestoreFeedPosition: false,
  totalPostLimit: null,
  lastPostAnchor: null,
};

const feedSlice = createSlice({
  name: 'feed',
  initialState,
  reducers: {
    updateActiveFilter: (state, { payload }) => {
      state.activeFilter = payload;
    },
    updateFeedFilters: (state, { payload }) => {
      state.feedFilters = payload;
    },
    setFeedPosts: (state, { payload }) => {
      state.posts = payload;
    },
    appendFeedPosts: (state, { payload }) => {
      state.posts = [...state.posts, ...payload];
    },
    removeFeedPost: (state, { payload }) => {
      state.posts = state.posts.filter((post) => post.id !== payload);
    },
    updatePostLikes: (state, { payload }) => {
      const { postId, likeId, action } = payload;
      const postIndex = state.posts.findIndex((post) => post.id === postId);
      if (postIndex !== -1) {
        if (action === 'add') {
          state.posts[postIndex].like_id = likeId;
          state.posts[postIndex].numberOfLikes = (state.posts[postIndex].numberOfLikes || 0) + 1;
        } else if (action === 'delete') {
          state.posts[postIndex].like_id = null;
          state.posts[postIndex].numberOfLikes = (state.posts[postIndex].numberOfLikes || 0) - 1;
        }
      }
    },
    updatePostComments: (state, { payload }) => {
      const { action, comment } = payload;
      const postIndex = state.posts.findIndex((post) => post.id === comment?.post_id);
      if (postIndex !== -1) {
        switch (action) {
          case 'add':
            state.posts[postIndex].comments = [...state.posts[postIndex].comments, comment];
            break;
          case 'delete':
            state.posts[postIndex].comments = state.posts[postIndex].comments.filter((c) => c.id !== comment.id);
            break;
          case 'update':
            state.posts[postIndex].comments = state.posts[postIndex].comments.map((c) => (c.id === comment.id ? { ...c, ...comment } : c));
            break;
          default:
            break;
        }
      }
    },
    updatePostCommentLikes: (state, { payload }) => {
      const { commentId, likeId, action } = payload;
      const postIndex = state.posts.findIndex((post) => post.comments.some((c) => c.id === commentId));
      if (postIndex === -1) {
        return;
      }
      const commentIndex = state.posts[postIndex].comments.findIndex((c) => c.id === commentId);
      if (commentIndex === -1) {
        return;
      }
      if (action === 'add') {
        state.posts[postIndex].comments[commentIndex].like_id = likeId;
        state.posts[postIndex].comments[commentIndex].numberOfLikes = (state.posts[postIndex].comments[commentIndex].numberOfLikes || 0) + 1;
      } else if (action === 'delete') {
        state.posts[postIndex].comments[commentIndex].like_id = null;
        state.posts[postIndex].comments[commentIndex].numberOfLikes = (state.posts[postIndex].comments[commentIndex].numberOfLikes || 0) - 1;
      }
    },
    updatePostSubComments: (state, { payload }) => {
      const { action, comment } = payload;
      const postIndex = state.posts.findIndex((post) => post.comments.some((c) => c.id === comment?.comment_id));
      if (postIndex === -1) {
        return;
      }
      const commentIndex = state.posts[postIndex].comments.findIndex((c) => c.id === comment?.comment_id);
      if (commentIndex === -1) {
        return;
      }
      switch (action) {
        case 'add':
          state.posts[postIndex].comments[commentIndex].comments = [...state.posts[postIndex].comments[commentIndex].comments, comment];
          break;
        case 'delete': {
          const updatedComments = state.posts[postIndex].comments[commentIndex].comments.filter((c) => c.id !== comment.id);
          state.posts[postIndex].comments[commentIndex].comments = updatedComments;
          break;
        }
        case 'update': {
          const updatedComments = state.posts[postIndex].comments[commentIndex].comments.map((c) => (c.id === comment.id ? { ...c, ...comment } : c));
          state.posts[postIndex].comments[commentIndex].comments = updatedComments;
          break;
        }
        default:
          break;
      }
    },
    updateLastPostAnchor: (state, { payload }) => {
      state.lastPostAnchor = payload;
    },
    setShouldRestoreFeedPosition: (state, { payload }) => {
      state.shouldRestoreFeedPosition = payload;
    },
    setTotalPostLimit: (state, { payload }) => {
      state.totalPostLimit = payload;
    },
  },
});

export const {
  updateActiveFilter,
  updateFeedFilters,
  setFeedPosts,
  appendFeedPosts,
  removeFeedPost,
  updatePostLikes,
  updatePostComments,
  updatePostCommentLikes,
  updatePostSubComments,
  updateLastPostAnchor,
  setShouldRestoreFeedPosition,
  setTotalPostLimit,
} = feedSlice.actions;

export const feedSelector = (state) => state.feed;

export default feedSlice.reducer;
